import { useState, useRef } from 'react'
import {
    User,
    Maximize2,
    X,
    ZoomIn,
    ZoomOut,
    RotateCcw,
    AlertTriangle,
    HelpCircle,
} from 'lucide-react'

import Overlay from '../Overlay'
import { trackEvent } from '../../utils/analytics'

const formatDate = (iso) => {
    if (!iso) return null
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
}

const formatGeoLocation = (geo) => {
    if (!geo) return null
    return [geo.city?.name, geo.region?.name, geo.country?.name].filter(Boolean).join(', ')
}

const Field = ({ label, value }) => (
    <div className="flex flex-col gap-0.5">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.18em]">{label}</p>
        {value
            ? <p className="text-[14px] font-medium text-slate-800 break-words min-w-0">{value}</p>
            : <div className="h-2.5 w-16 rounded bg-slate-300 mt-0.5" />
        }
    </div>
)

const StatusBadge = ({ valid, validText, invalidText, pending }) => {
    // Until the result data is retrieved we don't know the real status, so show a
    // neutral skeleton instead of defaulting to the red "Not Clear" state.
    if (pending) {
        return (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 w-fit border shadow-sm bg-slate-100 border-slate-200">
                <div className="w-1.5 h-1.5 rounded-full shrink-0 bg-slate-300 animate-pulse" />
                <div className="h-2.5 w-32 rounded bg-slate-200 animate-pulse" />
            </div>
        )
    }

    return (
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wider mb-4 w-fit border shadow-sm
            ${valid
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 shadow-emerald-100'
                : 'bg-red-50 text-red-600 border-red-200 shadow-red-100'
            }`}>
            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${valid ? 'bg-emerald-500' : 'bg-red-500'}`} />
            {valid ? validText : invalidText}
        </div>
    )
}

const IDCard = ({ data, referenceId, viewerRole }) => {
    const [photoExpanded, setPhotoExpanded] = useState(false)

    // Zoom / pan state for the photo lightbox.
    const [scale, setScale] = useState(1)
    const [offset, setOffset] = useState({ x: 0, y: 0 })
    const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, originX: 0, originY: 0 })

    const MIN_SCALE = 1
    const MAX_SCALE = 5

    const resetZoom = () => {
        setScale(MIN_SCALE)
        setOffset({ x: 0, y: 0 })
    }

    const closePhoto = () => {
        setPhotoExpanded(false)
        resetZoom()
    }

    const zoomBy = (delta) => {
        setScale((prev) => {
            const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number((prev + delta).toFixed(2))))
            if (next === MIN_SCALE) setOffset({ x: 0, y: 0 })
            return next
        })
    }

    const handleWheel = (e) => {
        zoomBy(e.deltaY < 0 ? 0.25 : -0.25)
    }

    const handlePointerDown = (e) => {
        if (scale <= MIN_SCALE) return
        dragRef.current = {
            active: true,
            moved: false,
            startX: e.clientX,
            startY: e.clientY,
            originX: offset.x,
            originY: offset.y,
        }
    }

    const handlePointerMove = (e) => {
        if (!dragRef.current.active) return
        const dx = e.clientX - dragRef.current.startX
        const dy = e.clientY - dragRef.current.startY
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true
        setOffset({ x: dragRef.current.originX + dx, y: dragRef.current.originY + dy })
    }

    const handlePointerUp = () => {
        dragRef.current.active = false
    }

    // A drag should not be treated as a backdrop click that closes the view.
    const handleBackdropClick = () => {
        if (dragRef.current.moved) {
            dragRef.current.moved = false
            return
        }
        closePhoto()
    }

    const { fullName, yearOfBirth, result } = data || {}
    const identity = result?.identity
    const employment = result?.employment

    // No result yet — the status badges should stay neutral rather than red.
    const pending = !result

    const checkedOnFormatted = formatDate(result?.checkedOn)
    const checkExpiresFormatted = formatDate(result?.validUntil)
    const checkedAtFormatted = formatGeoLocation(result?.geoLocation)

    // A check is expired once its validUntil date is in the past.
    const isExpired = result?.validUntil
        ? new Date(result.validUntil).getTime() < Date.now()
        : false

    // The "Check Expires" block (and its optional "Expired" badge) is shown
    // only when both the identity and employment verifications are Clear.
    const showCheckExpires = result?.identityStatus === 'Clear'
        && result?.employmentStatus === 'Clear'

    const displayRef = result?.referenceNumber
        || (referenceId ? referenceId.replace(/-/g, '').toUpperCase().slice(0, 12) : 'ABC141BC')

    // Encode whichever URL this view is currently served from — it may be the
    // public PreCheckView link or the authenticated PreCheckResultView page.
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(window.location.href)}&bgcolor=ffffff&color=0047ab&margin=3`

    return (
        <>
            <div className="w-full max-w-6xl">
                <div
                    className="flex flex-col rounded-2xl overflow-hidden border border-white/10"
                    style={{ boxShadow: '0 0 0 1px rgba(0,71,171,0.08), 0 40px 80px -20px rgba(0,0,0,0.55), 0 20px 40px -10px rgba(0,71,171,0.28), 0 0 100px -20px rgba(0,71,171,0.18)' }}
                >

                    {/* Top accent bar */}
                    <div className="h-1 bg-gradient-to-r from-[#0047AB] via-sky-300 to-[#0047AB]" />

                    {/* Revoked by a later USCIS check — the card is no longer valid. */}
                    {result?.revocation?.revoked && (
                        <div className="flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            <p className="text-[13px] font-bold uppercase tracking-[0.16em]">
                                Revoked{formatDate(result.revocation.revokedAt) ? ` — ${formatDate(result.revocation.revokedAt)}` : ''} · Not valid for employment verification
                            </p>
                        </div>
                    )}

                    {/* Header */}
                    <div className="shrink-0 border-b border-slate-200/60 px-8 pt-4 pb-3" style={{ background: 'linear-gradient(160deg, #ffffff 0%, #f0f5ff 100%)' }}>

                        {/* Top row: logo centered, QR / reference pinned right */}
                        <div className="flex items-start">
                            {/* Left spacer — mirrors the right column width to keep logo truly centered */}
                            <div className="flex-1 flex items-center justify-start self-start h-[110px]">
                                {viewerRole === 'client' && (
                                    <a
                                        href="/trucheck-guide"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() =>
                                            trackEvent("trucheck_how_to_use_click", { source: "trucheck_card" })
                                        }
                                        className="inline-flex items-center gap-1.5 whitespace-nowrap cursor-pointer select-none rounded-full border pl-2.5 pr-4 py-2
        text-[11px] font-bold uppercase tracking-[0.04em] text-[#1a4fb0] bg-[#dbe9fb] border-[#b7cff2] transition-all duration-200
        hover:bg-[#0047AB] hover:text-white hover:border-[#0047AB] hover:shadow-lg hover:shadow-[#0047AB]/30
        active:bg-[#003580] active:border-[#003580] active:scale-[0.97]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0047AB]/50 focus-visible:ring-offset-2"
                                    >
                                        <HelpCircle className="w-4 h-4 shrink-0" strokeWidth={2.25} aria-hidden="true" />
                                        How to Maximize Value from TruCheck™
                                    </a>
                                )}
                            </div>

                            <div className="relative shrink-0">
                                <img src="/trucheck.png" alt="TruCheck" className="h-[110px] w-auto object-contain" />
                                <a
                                    href="https://verifiedid.co"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() =>
                                        trackEvent("trucheck_referral_click", {
                                            source: "trucheck_card",
                                            destination: "https://verifiedid.co",
                                        })
                                    }
                                    className="absolute bottom-0 right-0 text-[13px] font-medium tracking-[0.18em] text-slate-400 leading-none underline underline-offset-2 decoration-slate-300 hover:text-slate-600 hover:decoration-slate-500 transition-colors"
                                >
                                    by <span className="font-semibold text-slate-500">VerifiedID</span>
                                </a>
                            </div>

                            {/* Reference + QR */}
                            <div className="flex-1 flex justify-end">
                                <div className="flex flex-col items-end gap-2.5">
                                    <div className="flex flex-col items-start text-left">
                                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.22em] mb-1">
                                            Reference Number
                                        </p>
                                        <p className="w-full text-[15px] font-bold text-slate-700 font-mono tracking-[0.2em] flex justify-between">
                                            {String(displayRef).split('').map((char, i) => (
                                                <span key={i}>{char}</span>
                                            ))}
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-center gap-1 p-2 rounded-xl bg-slate-50 border border-slate-200/80 shadow-sm">
                                        <img
                                            src={qrUrl}
                                            alt="Scan to Verify"
                                            className="w-[90px] h-[90px]"
                                        />
                                        <p className="text-[9px] font-medium text-slate-400 uppercase tracking-[0.18em]">
                                            Scan to Verify
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Centered title at bottom of header */}
                        <p className="text-[22px] tracking-wide text-center mt-1" style={{ color: '#0047AB' }}>
                            Identity & Employability Check
                        </p>
                    </div>

                    {/* Body */}
                    <div className="flex" style={{ background: 'linear-gradient(160deg, #eef3fb 0%, #e8eef8 50%, #f1f5f9 100%)' }}>

                        {/* Col 1: Photo */}
                        <div className="w-1/3 shrink-0 flex flex-col items-center justify-center gap-3 p-5 border-r border-[#0047AB]/10" style={{ background: 'linear-gradient(180deg, #dce8fa 0%, #eaf0fb 60%, #e8eef8 100%)' }}>
                            <div
                                className={`w-full aspect-[3/4] rounded-xl overflow-hidden border border-[#0047AB]/20 bg-slate-100 flex flex-col items-center justify-center gap-1.5 relative group ${data?.faceImageUrl ? 'cursor-zoom-in' : ''}`}
                                style={{ boxShadow: '0 8px 24px -6px rgba(0,71,171,0.18), 0 2px 8px -2px rgba(0,0,0,0.1)' }}
                                onClick={() => data?.faceImageUrl && setPhotoExpanded(true)}
                            >
                                {data?.faceImageUrl ? (
                                    <>
                                        <img
                                            src={data.faceImageUrl}
                                            alt="ID portrait"
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-200 flex items-center justify-center">
                                            <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 drop-shadow-lg" />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <User className="w-8 h-8 text-slate-300" />
                                        <span className="text-[9px] text-slate-400 tracking-wide">Photo</span>
                                    </>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/80">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                <p className="text-[10px] font-semibold text-emerald-700 tracking-wide uppercase">Live Photo</p>
                            </div>
                        </div>

                        {/* Cols 2 & 3 + Checked info */}
                        <div className="flex-1 flex flex-col">
                            <div className="flex flex-1">
                                {/* Col 2: Photo ID Verification */}
                                <div className="w-1/2 shrink-0 px-6 py-5 border-r border-slate-200/80">
                                    <StatusBadge
                                        pending={pending}
                                        valid={result?.identityStatus === 'Clear'}
                                        validText="Clear"
                                        invalidText="Review Required"
                                    />
                                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#0047AB', opacity: 0.55 }}>
                                        Photo ID Verification
                                    </p>
                                    <div className="space-y-4">
                                        <Field label="Full Name" value={fullName} />
                                        {/* Year of Birth is withheld from client/candidate audiences (backend
                                            returns null) — the field is still shown, rendered as a redacted
                                            placeholder just like "Last 4 SSN". */}
                                        <Field label="Year of Birth" value={yearOfBirth} />
                                        {identity?.usedDocuments?.length > 0 && <Field label="Documents Used" value={identity.usedDocuments.join(', ')} />}
                                    </div>
                                </div>

                                {/* Col 3: Employability Verification */}
                                <div className="w-1/2 shrink-0 px-6 py-5">
                                    <StatusBadge
                                        pending={pending}
                                        valid={result?.employmentStatus === 'Clear'}
                                        validText="Clear"
                                        invalidText="Review Required"
                                    />
                                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#0047AB', opacity: 0.55 }}>
                                        Employability Verification
                                    </p>
                                    <div className="space-y-4">
                                        {/* Backend supplies the label — client/candidate get a non-revealing
                                            "Employment Authorized in the US" / "Cannot Determine"; QA/ADMIN get
                                            the real work status. Rendered the same way for all. */}
                                        {result?.workStatus && <Field label="Work Status" value={result?.workStatus?.label} />}
                                        {/* Last 4 SSN — with an alert when QA flagged it "Cannot Be Validated"
                                            (could not be verified through the commercial databases). */}
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-1.5">
                                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.18em]">Last 4 SSN</p>
                                                {result?.ssnCouldNotBeValidated && (
                                                    <div className="relative group/ssn flex items-center">
                                                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 cursor-help shrink-0" />
                                                        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 hidden group-hover/ssn:block w-60 z-20 rounded-lg bg-slate-800 text-white text-[11px] leading-snug px-3 py-2 shadow-lg">
                                                            Last 4 SSN could not be validated using commercial Databases. This does not necessarily mean that the SSN does not exist
                                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {result?.lastFourSSN
                                                ? <p className="text-[14px] font-medium text-slate-800 break-words min-w-0">{result.lastFourSSN}</p>
                                                : <div className="h-2.5 w-16 rounded bg-slate-300 mt-0.5" />}
                                        </div>
                                        {employment?.usedDocuments?.length > 0 && <Field label="Documents Used" value={employment.usedDocuments.join(', ')} />}
                                    </div>
                                </div>
                            </div>

                            {/* Checked info — only under Identity & Employability columns */}
                            <div className="border-t border-[#0047AB]/10 px-8 py-5" style={{ background: 'linear-gradient(90deg, rgba(0,71,171,0.04) 0%, rgba(0,71,171,0.02) 100%)' }}>
                                <div className="flex justify-between items-start">
                                    <Field label="Checked On" value={checkedOnFormatted} />
                                    {showCheckExpires && (
                                        <div className="flex flex-col gap-0.5 items-end text-right">
                                            <Field label="Check Expires" value={checkExpiresFormatted} />
                                            {isExpired && (
                                                <div className="mt-1.5 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 border border-red-200 shadow-sm shadow-red-100">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                                    <span className="text-[13px] font-bold text-red-600 uppercase tracking-wider">
                                                        Expired
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {checkedAtFormatted && (
                                    <div className="mt-4">
                                        <Field label="Checked At" value={checkedAtFormatted} />
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* Footer */}
                    <div className="shrink-0 border-t border-[#0047AB]/10 px-6 py-3" style={{ background: 'linear-gradient(90deg, #f0f5ff 0%, #f8faff 50%, #f0f5ff 100%)' }}>
                        <p className="text-[12px] text-slate-400/80 text-center tracking-wide">
                            The redacted fields are securely stored and verified in our system. They are intentionally not displayed on this screen.
                        </p>
                    </div>

                </div>
            </div >

            {/* Photo lightbox modal */}
            {
                photoExpanded && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm overflow-hidden"
                        onClick={handleBackdropClick}
                        onWheel={handleWheel}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerUp}
                    >
                        <img
                            src={data.faceImageUrl}
                            alt="ID portrait"
                            draggable={false}
                            className="max-h-[85vh] max-w-[90vw] w-auto rounded-2xl shadow-[0_30px_60px_rgba(0,0,0,0.5)] border border-white/10 select-none"
                            style={{
                                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                                cursor: scale > MIN_SCALE ? (dragRef.current.active ? 'grabbing' : 'grab') : 'default',
                                transition: dragRef.current.active ? 'none' : 'transform 0.15s ease-out',
                            }}
                            onClick={e => e.stopPropagation()}
                            onPointerDown={handlePointerDown}
                        />

                        <button
                            onClick={(e) => { e.stopPropagation(); closePhoto() }}
                            className="absolute top-4 right-4 z-10 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-xl hover:bg-slate-100 transition-colors"
                        >
                            <X className="w-4 h-4 text-slate-600" />
                        </button>

                        {/* ZOOM CONTROLS */}
                        <div
                            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 bg-black/50 rounded-full px-2 py-1.5"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                className="p-1.5 rounded-full text-white hover:bg-white/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                onClick={() => zoomBy(-0.25)}
                                disabled={scale <= MIN_SCALE}
                                aria-label="Zoom out"
                            >
                                <ZoomOut className="w-5 h-5" />
                            </button>
                            <span className="text-white text-sm font-medium w-12 text-center select-none">
                                {Math.round(scale * 100)}%
                            </span>
                            <button
                                className="p-1.5 rounded-full text-white hover:bg-white/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                onClick={() => zoomBy(0.25)}
                                disabled={scale >= MAX_SCALE}
                                aria-label="Zoom in"
                            >
                                <ZoomIn className="w-5 h-5" />
                            </button>
                            <button
                                className="p-1.5 rounded-full text-white hover:bg-white/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                onClick={resetZoom}
                                disabled={scale === MIN_SCALE && offset.x === 0 && offset.y === 0}
                                aria-label="Reset zoom"
                            >
                                <RotateCcw className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )
            }
        </>
    )
}

const IdResultView = ({ data, referenceId, isFetching, error, viewerRole }) => {
    if (error) {
        // A card is only issued for a Clear result (client agreement §2.3); the backend answers
        // `cardUnavailable` for any other result, including a link shared before the result changed.
        const cardUnavailable = !!error?.response?.data?.cardUnavailable
        // A card revoked by a later USCIS check says so explicitly.
        const revoked = !!error?.response?.data?.revoked
        const revokedOn = formatDate(error?.response?.data?.revokedAt)
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center">
                <div className="bg-white rounded-xl border border-red-200 p-8 text-center text-red-600 shadow max-w-md">
                    {revoked ? (
                        <>
                            <p className="text-lg font-bold uppercase tracking-wider">TruCheck Card Revoked</p>
                            <p className="mt-2 text-sm text-red-700">
                                This TruCheck card was invalidated{revokedOn ? ` on ${revokedOn}` : ""} after a later
                                USCIS check reported a change in employment authorization. It is no longer valid.
                            </p>
                        </>
                    ) : cardUnavailable
                        ? "This TruCheck card is not available."
                        : "Failed to load verification details."}
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0d2147 40%, #0a1628 100%)' }}>
            <IDCard data={data} referenceId={referenceId} viewerRole={viewerRole} />
            {isFetching && <Overlay />}
        </div>
    )
}

export default IdResultView
